import ExcelJS from "exceljs";

interface ExportExcelOptions {
	data: Record<string, any>[];
	columns: { key: string; label: string }[];
	title: string;
	subtitle?: string;
	logoBase64?: string;
	sheetName?: string;
	fileName?: string;
}

export async function exportExcelWithStyle({
	data,
	columns,
	title,
	subtitle,
	logoBase64,
	sheetName = "Relatório",
	fileName = "Relatorio.xlsx",
}: ExportExcelOptions) {
	const workbook = new ExcelJS.Workbook();
	const sheet = workbook.addWorksheet(sheetName);

	// Desativa linhas de grade
	sheet.views = [{ showGridLines: false }];

	// Adiciona imagem se houver logo
	if (logoBase64) {
		const imageId = workbook.addImage({
			base64: logoBase64,
			extension: "png",
		});
		sheet.addImage(imageId, {
			tl: { col: 0, row: 0 },
			ext: { width: 180, height: 50 },
		});
	}

	const startRow = 5;

	// Título (linha 1), mesclando da coluna B até o final
	sheet.mergeCells(1, 2, 1, columns.length + 1);
	const titleCell = sheet.getCell(1, 2);
	titleCell.value = title;
	titleCell.font = { name: "Calibri", bold: true, size: 18 };
	titleCell.alignment = { vertical: "middle", horizontal: "center" };

	// Subtítulo + total de linhas (linha 2)
	sheet.mergeCells(2, 2, 2, columns.length + 1);
	const subtitleCell = sheet.getCell(2, 2);
	subtitleCell.value = `${subtitle ?? ""} | Total de linhas: ${data.length}`;
	subtitleCell.font = { name: "Calibri", italic: true, size: 11 };
	subtitleCell.alignment = { vertical: "middle", horizontal: "center" };

	// Cabeçalho (linha 5), iniciando na coluna A
	const headerRow = sheet.getRow(startRow);
	headerRow.values = columns.map((col) => col.label);
	headerRow.eachCell((cell) => {
		cell.fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: "FF0078D7" }, // azul
		};
		cell.font = {
			name: "Calibri",
			bold: true,
			color: { argb: "FFFFFFFF" }, // branco
		};
		cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
		cell.border = {
			top: { style: "thin" },
			left: { style: "thin" },
			bottom: { style: "thin" },
			right: { style: "thin" },
		};
	});

	// Dados
	data.forEach((rowData, index) => {
		const row = sheet.getRow(startRow + 1 + index);
		columns.forEach((col, colIndex) => {
			const cell = row.getCell(colIndex + 1);
			cell.value = rowData[col.key];
			cell.font = { name: "Calibri" };
			cell.alignment = { vertical: "middle", horizontal: "left" };

			// Zebra
			if (index % 2 === 1) {
				cell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "FFF2F2F2" },
				};
			}
		});
	});

	// Largura das colunas
	columns.forEach((col, i) => {
		const maxLength = Math.max(...data.map((d) => String(d[col.key] ?? "").length), col.label.length);
		sheet.getColumn(i + 1).width = Math.min(maxLength + 2, 50);
	});

	// Gera arquivo
	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}
